import { uiFormat, uiIn, uiText, useAccountLocale } from '../../i18n';
import { useEffect, useRef, useState } from 'react';
import { endpoints } from '../../api';
import { uuid } from '../../api/client';
import type { AccountErasure, AccountExport } from '../../api/account';
import { CloneDialog } from '../clone/CloneDialog';
import '../account/server-account.css';
function saved<T>(key: string): T | null {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null') as T | null;
  } catch {
    return null;
  }
}
/** Состояние операции словом; неизвестный код сервера — как пришёл */
const STATUS: Record<string, string> = {
  done: 'готова',
  partial: 'готова частично',
  queued: 'в очереди',
  running: 'идёт',
  completed: 'завершено',
  failed: 'не удалось',
};
const statusText = (status: string) => uiText(STATUS[status] ?? status);
/** Слово, которое вводят для удаления: на языке профиля (русское тоже подходит) */
const eraseWord = () => uiIn('слово подтверждения', 'УДАЛИТЬ');
const eraseTyped = (phrase: string) => [eraseWord(), 'УДАЛИТЬ'].includes(phrase.trim());
export function AccountData() {
  useAccountLocale();
  const [exported, setExported] = useState<AccountExport | null>(() => saved('steos:export:operation'));
  const [erasure, setErasure] = useState<AccountErasure | null>(() => saved('steos:erasure:operation'));
  const [url, setUrl] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [confirm, setConfirm] = useState(false),
    [phrase, setPhrase] = useState('');
  const exportKey = useRef<string | null>(saved('steos:export:key'));
  const eraseKey = useRef<string | null>(saved('steos:erasure:key'));
  const running = useRef(false);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  useEffect(() => {
    const id = exported?.operation_id;
    if (!id || ['done', 'partial', 'failed'].includes(exported.status)) return;
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = async () => {
      try {
        const value = await endpoints.account.exportStatus(id, controller.signal);
        if (controller.signal.aborted) return;
        setExported(value);
        setError('');
        sessionStorage.setItem('steos:export:operation', JSON.stringify(value));
        if (!['done', 'partial', 'failed'].includes(value.status)) timer = window.setTimeout(() => void poll(), 3000);
      } catch {
        if (!controller.signal.aborted) {
          setError('Не удалось проверить выгрузку. Обновите страницу: продолжим прежнюю операцию.');
          timer = window.setTimeout(() => void poll(), 5000);
        }
      }
    };
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [exported?.operation_id, exported?.status]);
  useEffect(() => {
    const id = erasure?.operation_id,
      receipt = erasure?.status_receipt;
    if (!id || !receipt || erasure.status === 'completed') return;
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = async () => {
      try {
        const value = await endpoints.account.erasureStatus(id, receipt, controller.signal);
        if (controller.signal.aborted) return;
        const next = { ...value, status_receipt: receipt };
        setErasure(next);
        setError('');
        sessionStorage.setItem('steos:erasure:operation', JSON.stringify(next));
        if (value.status !== 'completed') timer = window.setTimeout(() => void poll(), 5000);
      } catch {
        if (!controller.signal.aborted) {
          setError('Статус удаления временно недоступен. Квитанция сохранена в этой вкладке.');
          timer = window.setTimeout(() => void poll(), 5000);
        }
      }
    };
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [erasure?.operation_id, erasure?.status_receipt, erasure?.status]);
  async function act(kind: 'export' | 'download' | 'erase') {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError('');
    try {
      if (kind === 'export') {
        exportKey.current ??= uuid();
        sessionStorage.setItem('steos:export:key', JSON.stringify(exportKey.current));
        const value = await endpoints.account.createExport(exportKey.current);
        setExported(value);
        sessionStorage.setItem('steos:export:operation', JSON.stringify(value));
      } else if (kind === 'download' && exported) {
        const blob = await endpoints.account.exportDownload(exported.operation_id);
        setUrl(URL.createObjectURL(blob));
      } else if (kind === 'erase' && eraseTyped(phrase)) {
        eraseKey.current ??= uuid();
        sessionStorage.setItem('steos:erasure:key', JSON.stringify(eraseKey.current));
        const value = await endpoints.account.erase(eraseKey.current);
        setErasure(value);
        sessionStorage.setItem('steos:erasure:operation', JSON.stringify(value));
        setConfirm(false);
      }
    } catch {
      setError(
        kind === 'download'
          ? 'Архив недоступен или срок ссылки истёк. Можно запросить новую выгрузку.'
          : 'Операция не подтверждена. Повтор использует прежний ключ.',
      );
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  return (
    <section className="server-account-card">
      <h2>{uiText('Мои данные')}</h2>
      <button type="button" disabled={busy || !!erasure} onClick={() => void act('export')}>
        {uiText(exported ? 'Проверить выгрузку' : 'Выгрузить мои данные')}
      </button>
      {exported && (
        <p role="status">{uiFormat('Выгрузка: {0}', statusText(exported.status))}</p>
      )}
      {exported?.download_url && (
        <button type="button" disabled={busy} onClick={() => void act('download')}>
          {uiText('Подготовить ZIP для скачивания')}
        </button>
      )}
      {exported && ['done', 'partial', 'failed'].includes(exported.status) && (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            exportKey.current = null;
            setExported(null);
            setUrl('');
            sessionStorage.removeItem('steos:export:key');
            sessionStorage.removeItem('steos:export:operation');
          }}
        >
          {uiText('Новая выгрузка')}
        </button>
      )}
      {url && (
        <a href={url} download="steos-account-data.zip">
          {uiText('Скачать ZIP')}
        </a>
      )}
      <button
        type="button"
        className="server-account-danger"
        disabled={busy || !!erasure}
        onClick={() => {
          setConfirm(true);
          setPhrase('');
        }}
      >
        {uiText('Удалить аккаунт Mini App')}
      </button>
      {erasure && (
        <p role="status">{uiFormat('Удаление: {0}. STEOS ID и финансовые квитанции сохраняются.', statusText(erasure.status))}</p>
      )}
      {confirm && (
        <CloneDialog label={uiText('Удаление аккаунта Mini App')} busy={busy} onClose={() => setConfirm(false)}>
          <h2>{uiText('Удаление аккаунта Mini App')}</h2>
          <p>
            {uiText(
              'Фото, голоса, близкие и данные Mini App будут удалены во всех сервисах. STEOS ID и финансовые квитанции останутся. Отменить удаление нельзя.',
            )}
          </p>
          <label>
            {uiFormat('Введите {0}', eraseWord())}
            <input
              aria-label={uiFormat('Введите {0}', eraseWord())}
              value={phrase}
              disabled={busy}
              onChange={event => setPhrase(event.target.value)}
            />
          </label>
          <div className="server-account-actions">
            <button type="button" disabled={busy} onClick={() => setConfirm(false)}>
              {uiText('Отмена')}
            </button>
            <button type="button" disabled={busy || !eraseTyped(phrase)} onClick={() => void act('erase')}>
              {uiText('Подтвердить удаление аккаунта')}
            </button>
          </div>
        </CloneDialog>
      )}
      {error && (
        <p role="alert" className="server-account-error">
          {uiText(error)}
        </p>
      )}
    </section>
  );
}
